import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { creditValueCentsToCredits, formatCreditsValue, formatUsd, creditsToUsd } from '@/utils/credits';
import { getCoachWallet, getCoachPacks, coachCheckout, coachTransferCredits } from '@/lib/coreApi';
import type { CreditPack } from '@/lib/coreApi';
import SupportPanel from '@/components/support/SupportPanel';
import { CurrentToolsCard } from '@/components/ai/CurrentToolsCard';

type WalletResponse = {
  wallet: {
    balanceCredits: number;
  };
};

export default function CoachWallet() {
  const [wallet, setWallet] = useState<WalletResponse['wallet'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipientType, setRecipientType] = useState<'share' | 'user'>('share');
  const [shareCode, setShareCode] = useState('');
  const [recipientUserId, setRecipientUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [transferStatus, setTransferStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [transferMessage, setTransferMessage] = useState('');
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState<string | null>(null);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCoachWallet();
      setWallet(res?.data?.wallet ?? null);
    } catch (err) {
      console.warn('[CoachWallet] unable to load wallet', err);
      setWallet(null);
      setError(err instanceof Error ? err.message : 'Unable to load wallet');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPacks = useCallback(async () => {
    setPacksLoading(true);
    setPacksError(null);
    try {
      const res = await getCoachPacks();
      setPacks(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch (err) {
      console.warn('[CoachWallet] unable to load packs', err);
      setPacks([]);
      setPacksError(err instanceof Error ? err.message : 'Unable to load credit packs');
    } finally {
      setPacksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadPacks();
  }, [loadWallet, loadPacks]);

  const startCheckout = async (pack: CreditPack) => {
    setBuyingPack(pack.id);
    try {
      const res = await coachCheckout(pack.name);
      if (res?.data?.url && typeof window !== 'undefined') {
        window.location.assign(res.data.url);
      }
    } catch (err) {
      console.warn('[CoachWallet] checkout failed', err);
      setPacksError(err instanceof Error ? err.message : 'Unable to start checkout');
    } finally {
      setBuyingPack(null);
    }
  };

  const balanceLabel = useMemo(() => formatCreditsValue(wallet?.balanceCredits ?? 0), [wallet?.balanceCredits]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedAmount = Number.parseInt(amount, 10);
    const cleanShareCode = shareCode.trim();
    const cleanUserId = recipientUserId.trim();

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setTransferStatus('error');
      setTransferMessage('Enter a positive credit amount.');
      return;
    }

    if (recipientType === 'share' && !cleanShareCode) {
      setTransferStatus('error');
      setTransferMessage('Enter a teacher share code.');
      return;
    }

    if (recipientType === 'user' && !cleanUserId) {
      setTransferStatus('error');
      setTransferMessage('Enter a Tower user ID.');
      return;
    }

    setTransferStatus('loading');
    setTransferMessage('');

    try {
      const payload: Record<string, unknown> = {
        amount: parsedAmount,
        note: note.trim() ? note.trim() : undefined,
        typeOut: 'coach_transfer',
        typeIn: 'coach_transfer_in',
        source: 'coach_wallet',
      };

      if (recipientType === 'share') {
        payload.shareCode = cleanShareCode;
      } else {
        payload.toUserId = cleanUserId;
      }

      await coachTransferCredits(payload as any);

      setTransferStatus('success');
      setTransferMessage('Transfer sent!');
      setAmount('');
      setNote('');
      if (recipientType === 'share') {
        setShareCode('');
      } else {
        setRecipientUserId('');
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('tower-wallet-refresh'));
      }

      await loadWallet();
    } catch (err) {
      console.warn('[CoachWallet] transfer failed', err);
      setTransferStatus('error');
      setTransferMessage(err instanceof Error ? err.message : 'Unable to send credits');
    }
  };

  return (
    <div className="PortalShell">
      <header className="PortalHeader">
        <h1>Coach Credits Wallet</h1>
        <p>Send Tower credits to teachers or teammates from your wallet.</p>
      </header>

      <section className="Card">
        <CurrentToolsCard role="coach" manageHref="/portal/coach/ai-tools" />
      </section>

      <section className="Card space-y-4">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-cocoa/60">Current balance</p>
            <p className="text-3xl font-semibold text-cocoa">{loading ? '—' : `${balanceLabel} credits`}</p>
          </div>
        </div>
        {error ? <p className="text-sm text-brick">{error}</p> : null}
        <p className="text-sm text-cocoa/70">
          Wallet credits are not cash. They fund Tower experiences for students.
        </p>
      </section>

      <section className="Card space-y-4">
        <h2 className="Title">Send credits</h2>
        <p className="text-sm text-cocoa/70">Choose a recipient and enter a credit amount.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recipient"
                value="share"
                checked={recipientType === 'share'}
                onChange={() => setRecipientType('share')}
              />
              Teacher share code
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recipient"
                value="user"
                checked={recipientType === 'user'}
                onChange={() => setRecipientType('user')}
              />
              Tower user ID
            </label>
          </div>

          {recipientType === 'share' ? (
            <label className="block text-sm font-medium text-cocoa">
              Share code
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
                value={shareCode}
                onChange={(event) => setShareCode(event.target.value.toUpperCase())}
                placeholder="PT-ABC123"
              />
            </label>
          ) : (
            <label className="block text-sm font-medium text-cocoa">
              Recipient user ID
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
                value={recipientUserId}
                onChange={(event) => setRecipientUserId(event.target.value)}
                placeholder="uuid"
              />
            </label>
          )}

          <label className="block text-sm font-medium text-cocoa">
            Credits to send
            <input
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))}
              placeholder="500"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </label>

          <label className="block text-sm font-medium text-cocoa">
            Optional note
            <input
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add a message"
            />
          </label>

          <button
            type="submit"
            className="Button primary w-full"
            disabled={transferStatus === 'loading'}
          >
            {transferStatus === 'loading' ? 'Sending…' : 'Send credits'}
          </button>
          {transferMessage ? (
            <p className={`text-sm ${transferStatus === 'error' ? 'text-brick' : 'text-emerald-600'}`}>
              {transferMessage}
            </p>
          ) : null}
        </form>
      </section>

      <section className="Card space-y-4">
        <h2 className="Title">Buy Tower Credits</h2>
        <p className="text-sm text-cocoa/70">Purchase credit packs to fund your team's experiences.</p>
        {packsError ? <p className="text-sm text-brick">{packsError}</p> : null}
        {packsLoading ? (
          <p className="text-sm text-cocoa/60">Loading packs…</p>
        ) : packs.length === 0 ? (
          <p className="text-sm text-cocoa/60">No credit packs available right now.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((pack) => {
              const credits = creditValueCentsToCredits(pack.credit_value_cents);
              return (
                <div key={pack.id} className="rounded-xl border border-sand bg-white p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-cocoa/60">{pack.name}</p>
                  <p className="text-2xl font-semibold text-cocoa">
                    ${(Number(pack.usd_price) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-cocoa/60">
                    {formatCreditsValue(credits)} credits (≈ {formatUsd(creditsToUsd(credits))})
                  </p>
                  <button
                    type="button"
                    onClick={() => startCheckout(pack)}
                    disabled={buyingPack === pack.id}
                    className="Button primary w-full"
                  >
                    {buyingPack === pack.id ? 'Redirecting…' : 'Buy pack'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <SupportPanel
        roleLabel="Coach"
        intro="Need help with wallets or transfers? The Tower support team is here."
        defaultCategory="coach_support"
        contextMeta={{ surface: 'coach_portal', wallet_context: 'locker_room' }}
        defaultMetadata={{ source: 'coach_wallet' }}
      />
    </div>
  );
}
